import { useEffect, useRef, useState } from "react";
import type { Terminal } from "@xterm/xterm";
import { wsUrl } from "../../../../lib/wsUrl";

/**
 * The terminal's WebSocket, as a hook (2026-07-15).
 *
 * Split from the component because the socket has a lifecycle the React tree
 * does not: it must open once per panel, survive re-renders, and — above all —
 * CLOSE when the panel goes away. The server kills the shell when its socket
 * drops, so failing to close here leaks a real OS process the user cannot see.
 * A `useEffect` cleanup is the only thing that reliably fires on unmount, which
 * is why the close lives here rather than in an event handler.
 */

export type TerminalStatus =
	| { state: "connecting" }
	| { state: "ready"; shell: string }
	| { state: "exited" }
	| { state: "error"; detail: string; code: string };

interface Options {
	/** The xterm instance to pipe output into. Null until it has mounted. */
	term: Terminal | null;
	/** Initial size. The shell is spawned AT this size — see the `start` frame. */
	rows: number;
	cols: number;
}

export function useTerminalSocket({ term, rows, cols }: Options) {
	const [status, setStatus] = useState<TerminalStatus>({ state: "connecting" });
	const wsRef = useRef<WebSocket | null>(null);
	// The first size is read through a ref so a resize during connect doesn't
	// re-run the effect and reconnect the socket (which would kill the shell).
	const sizeRef = useRef({ rows, cols });
	sizeRef.current = { rows, cols };

	useEffect(() => {
		if (!term) return;
		const ws = new WebSocket(wsUrl("/ws/terminal"));
		wsRef.current = ws;

		ws.onopen = () => {
			// The shell is spawned AT this size rather than at a guessed 24x80 and
			// resized a beat later — that makes the first prompt wrap at the wrong
			// column, which reads as a rendering bug.
			ws.send(JSON.stringify({ type: "start", ...sizeRef.current }));
		};

		ws.onmessage = (ev) => {
			let msg: Record<string, unknown>;
			try {
				msg = JSON.parse(ev.data as string);
			} catch {
				return;
			}
			switch (msg.type) {
				case "ready":
					setStatus({ state: "ready", shell: String(msg.shell ?? "") });
					break;
				case "output":
					term.write(String(msg.data ?? ""));
					break;
				case "exit":
					setStatus({ state: "exited" });
					break;
				case "error":
					// The server's refusals are ACTIONABLE (a contract denial names
					// the line to add). Surfacing `detail` verbatim is the whole
					// point — "connection failed" would throw that away.
					setStatus({
						state: "error",
						detail: String(msg.detail ?? "The terminal failed."),
						code: String(msg.code ?? "unknown"),
					});
					break;
			}
		};

		ws.onerror = () => {
			setStatus((cur) =>
				cur.state === "ready"
					? cur
					: { state: "error", detail: "Could not reach the terminal service.", code: "socket" },
			);
		};

		// Keystrokes -> shell.
		const typed = term.onData((data) => {
			if (ws.readyState === WebSocket.OPEN) {
				ws.send(JSON.stringify({ type: "input", data }));
			}
		});

		return () => {
			typed.dispose();
			// Closing the socket is what kills the shell server-side. Skip it and a
			// real process survives the panel, invisible, until the app exits.
			ws.close();
			wsRef.current = null;
		};
	}, [term]);

	/** Tell the shell its new window size. Without it the child keeps its
	 *  spawn-time dimensions and wraps at the wrong column after any resize. */
	const sendResize = (r: number, c: number) => {
		const ws = wsRef.current;
		if (ws && ws.readyState === WebSocket.OPEN) {
			ws.send(JSON.stringify({ type: "resize", rows: r, cols: c }));
		}
	};

	return { status, sendResize };
}
